/** Ordered creator-facing presentation for publish-readiness reasons. */
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

export interface PublishReasonAction {
  label: string;
  kind: "recheck" | "ask-cogo";
}
export interface HumanizedPublishReason {
  title: string;
  detail?: string;
  action?: PublishReasonAction;
}
type PublishCopyLocale = "en" | "ko";
type PublishReasonActionKind = PublishReasonAction["kind"];
interface PublishReasonPresentation {
  title: string;
  detail?: string;
  action?: PublishReasonActionKind;
}
type LocalizedPublishReasonCopy = Record<
  PublishCopyLocale,
  PublishReasonPresentation
>;
interface PublishReasonEntry {
  prefix: string;
  copy: LocalizedPublishReasonCopy;
}

// Button labels for the two publish-gate action kinds, in all five Studio
// locales. Deliberately a SEPARATE locale axis from `PublishCopyLocale`
// above (which only covers `en`/`ko` — the reason catalog's title/detail
// copy is still bilingual-only, out of this batch's scope): the label is
// looked up by the CALLER'S real `LocaleCode`, not by `presentationLocale`'s
// en/ko collapse, so ja/zh/zh-TW get their own button text even while the
// surrounding title/detail still fall back to English.
const ACTION_LABELS: Record<
  LocaleCode,
  Record<PublishReasonActionKind, string>
> = {
  en: { recheck: "Re-check readiness", "ask-cogo": "Review in Chat" },
  ko: { recheck: "다시 확인", "ask-cogo": "Coogo에게 해결 방법 묻기" },
  ja: { recheck: "再確認", "ask-cogo": "Coogoに解決方法を聞く" },
  zh: { recheck: "重新确认", "ask-cogo": "向 Coogo 咨询解决方法" },
  "zh-TW": { recheck: "重新確認", "ask-cogo": "向 Coogo 諮詢解決方法" },
};

function presentationLocale(locale: LocaleCode): PublishCopyLocale {
  return locale === "ko" ? "ko" : "en";
}

/** Implementation-authoritative bilingual catalog; keep this order. */
export const PUBLISH_REASON_CATALOG: readonly PublishReasonEntry[] = [
  {
    prefix: "Preview readiness gate is blocked.",
    copy: {
      en: {
        title: "Your app view isn't ready yet",
        detail: "This clears once Coogo finishes generating your app.",
      },
      ko: {
        title: "앱 화면을 아직 준비하지 못했습니다",
        detail: "앱 생성이 완료된 뒤 발행 준비 상태를 다시 확인하세요.",
      },
    },
  },
  {
    prefix: "Generated app readiness gate is not publish-ready.",
    copy: {
      en: {
        title: "Your app isn't finished building yet",
        detail: "Open Chat to finish generating the app.",
        action: "ask-cogo",
      },
      ko: {
        title: "앱 제작이 아직 끝나지 않았습니다",
        detail: "Coogo와 대화해 앱 제작을 마무리하세요.",
        action: "ask-cogo",
      },
    },
  },
  {
    prefix: "ActionFlow is required for this app plan",
    copy: {
      en: {
        title: "Your app needs its behavior wired up before it can publish",
        detail: "Open Chat to add the missing actions.",
        action: "ask-cogo",
      },
      ko: {
        title: "앱 동작을 연결해야 발행할 수 있습니다",
        detail: "Chat에서 누락된 동작을 추가하세요.",
        action: "ask-cogo",
      },
    },
  },
  {
    prefix: "signed manifest is required for",
    copy: {
      en: {
        title: "A signed release is required to publish this app",
        detail:
          "This security profile needs a signed manifest. Re-check once it has been generated.",
        action: "recheck",
      },
      ko: {
        title: "이 앱을 발행하려면 서명된 릴리스가 필요합니다",
        detail:
          "이 보안 프로필에는 서명된 매니페스트가 필요합니다. 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "wasm integrity is required for",
    copy: {
      en: {
        title: "A verified runtime is required to publish this app",
        detail:
          "This security profile needs a runtime-integrity check. Re-check once it has been generated.",
        action: "recheck",
      },
      ko: {
        title: "이 앱을 발행하려면 검증된 런타임이 필요합니다",
        detail:
          "이 보안 프로필에는 런타임 무결성 검사가 필요합니다. 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "policy bundle is required for",
    copy: {
      en: {
        title: "A security policy is required to publish this app",
        detail:
          "This security profile needs a policy bundle. Re-check once it has been generated.",
        action: "recheck",
      },
      ko: {
        title: "이 앱을 발행하려면 보안 정책이 필요합니다",
        detail:
          "이 보안 프로필에는 정책 번들이 필요합니다. 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "action allowlist is required for",
    copy: {
      en: {
        title: "An approved-actions list is required to publish this app",
        detail:
          "This security profile needs an action allowlist. Re-check once it has been generated.",
        action: "recheck",
      },
      ko: {
        title: "이 앱을 발행하려면 승인된 동작 목록이 필요합니다",
        detail:
          "이 보안 프로필에는 동작 허용 목록이 필요합니다. 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "datasource entitlements is required for",
    copy: {
      en: {
        title: "Data-access permissions are required to publish this app",
        detail:
          "This security profile needs datasource entitlements. Re-check once they have been granted.",
        action: "recheck",
      },
      ko: {
        title: "이 앱을 발행하려면 데이터 접근 권한이 필요합니다",
        detail:
          "이 보안 프로필에는 데이터 소스 권한이 필요합니다. 권한이 부여된 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "signed manifest is required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title: "Your app connects to live services — it needs a signed release",
        detail:
          "Re-check once the signed manifest for these connections has been generated.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 서비스에 연결하므로 서명된 릴리스가 필요합니다",
        detail: "이 연결의 서명된 매니페스트를 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "action allowlist is required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title:
          "Your app connects to live services — it needs an approved-actions list",
        detail:
          "Re-check once the action allowlist for these connections has been generated.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 서비스에 연결하므로 승인된 동작 목록이 필요합니다",
        detail: "이 연결의 동작 허용 목록을 생성한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "adapter entitlements are required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title:
          "Your app connects to live services — those connections need permissions",
        detail: "Re-check once the service permissions have been granted.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 서비스에 연결하므로 연결 권한이 필요합니다",
        detail: "서비스 권한이 부여된 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "credential policy is required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title:
          "Your app connects to live services — those connections need a credential policy",
        detail: "Re-check once the credential policy has been provisioned.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 서비스에 연결하므로 자격 증명 정책이 필요합니다",
        detail: "자격 증명 정책을 설정한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "network policy is required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title:
          "Your app connects to live services — those connections need a network policy",
        detail: "Re-check once the network policy has been provisioned.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 서비스에 연결하므로 네트워크 정책이 필요합니다",
        detail: "네트워크 정책을 설정한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "datasource entitlements are required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title: "Your app reads live data — it needs data-access permissions",
        detail: "Re-check once the datasource entitlements have been granted.",
        action: "recheck",
      },
      ko: {
        title: "앱이 실제 데이터를 읽으므로 데이터 접근 권한이 필요합니다",
        detail: "데이터 소스 권한이 부여된 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "adapter smoke proof is required before publishing adapter-backed runtime access",
    copy: {
      en: {
        title: "Your live-service connections need a quick health check",
        detail:
          "This runs when your connections are provisioned; re-check once it completes.",
        action: "recheck",
      },
      ko: {
        title: "실제 서비스 연결에 간단한 상태 점검이 필요합니다",
        detail:
          "연결을 설정할 때 이 점검을 실행합니다. 완료된 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "Dynamic/hybrid publish requires RPK1 RenderPack packaging evidence.",
    copy: {
      en: {
        title: "Your app's pages still need to be packaged for delivery",
        detail: "Re-check once the app has finished packaging.",
        action: "recheck",
      },
      ko: {
        title: "앱 화면을 배포용으로 패키징해야 합니다",
        detail: "앱 패키징을 마친 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "Dynamic publish requires ActionFlow readiness evidence from the ActionFlow core contract.",
    copy: {
      en: {
        title: "Your app's behavior still needs a readiness check",
        detail:
          "Attach a ready behavior contract with runtime or browser checks before publishing this dynamic app.",
      },
      ko: {
        title: "앱 동작의 발행 준비 상태를 확인해야 합니다",
        detail:
          "동적 앱을 발행하기 전에 런타임 또는 브라우저 검사 결과가 포함된 준비 상태의 동작 계약을 연결하세요.",
      },
    },
  },
  {
    prefix:
      "Hybrid publish requires ActionFlow readiness evidence from the ActionFlow core contract.",
    copy: {
      en: {
        title: "Your app's behavior still needs a readiness check",
        detail:
          "Attach a ready behavior contract with runtime or browser checks before publishing this hybrid app.",
      },
      ko: {
        title: "앱 동작의 발행 준비 상태를 확인해야 합니다",
        detail:
          "하이브리드 앱을 발행하기 전에 런타임 또는 브라우저 검사 결과가 포함된 준비 상태의 동작 계약을 연결하세요.",
      },
    },
  },
  {
    prefix: "ActionFlow readiness is",
    copy: {
      en: {
        title: "Your app's behavior contract is not ready yet",
        detail:
          "Publish requires the behavior contract status to be ready, with check references attached.",
      },
      ko: {
        title: "앱 동작 계약이 아직 준비되지 않았습니다",
        detail:
          "동작 계약의 상태가 '준비됨'이어야 하며, 검사 참조를 연결해야 합니다.",
      },
    },
  },
  {
    prefix:
      "Report/document publish requires report/export readiness evidence for every claimed output target.",
    copy: {
      en: {
        title: "Your report exports still need readiness checks",
        detail:
          "Attach report/export checks for each claimed output format before publishing this report app.",
      },
      ko: {
        title: "보고서 내보내기의 발행 준비 상태를 확인해야 합니다",
        detail:
          "이 보고서 앱을 발행하기 전에 제공한다고 표시한 각 출력 형식의 보고서 또는 내보내기 검사를 연결하세요.",
      },
    },
  },
  {
    prefix: "Report/export readiness is",
    copy: {
      en: {
        title: "Your report export contract is not ready yet",
        detail:
          "Publish requires the report/export contract status to be ready, with checks for every claimed output format.",
      },
      ko: {
        title: "보고서 내보내기 계약이 아직 준비되지 않았습니다",
        detail:
          "보고서 또는 내보내기 계약의 상태가 '준비됨'이어야 하며, 제공한다고 표시한 모든 출력 형식의 검사가 필요합니다.",
      },
    },
  },
  {
    prefix: "Ontology matching plan is missing",
    copy: {
      en: {
        title: "Your app needs source matching before it can publish",
        detail:
          "Coogo must attach deterministic source-matching checks before this generated app goes live.",
      },
      ko: {
        title: "앱을 발행하기 전에 원본 소스와의 연결을 확인해야 합니다",
        detail:
          "생성된 앱을 발행하기 전에 원본 소스와 연결되는지 확인하는 검사를 추가하세요.",
      },
    },
  },
  {
    prefix: "Ontology matching plan has no decisions",
    copy: {
      en: {
        title: "Your app needs source matching decisions before it can publish",
        detail:
          "Coogo must decide which page, block, or component assets were reused, adapted, composed, or backfilled.",
      },
      ko: {
        title: "앱을 발행하기 전에 원본 소스 연결 방식을 정해야 합니다",
        detail:
          "어떤 페이지, 블록, 구성 요소를 재사용·수정·조합·보완했는지 결정하세요.",
      },
    },
  },
  {
    prefix: "Ontology matching has unverifiable candidate decisions",
    copy: {
      en: {
        title: "Some generated assets still need source backfill",
        detail:
          "Publish stays blocked until candidate pages, blocks, or components are admitted into source checks.",
      },
      ko: {
        title: "일부 생성 결과의 원본 소스 근거를 보완해야 합니다",
        detail:
          "후보 페이지, 블록, 구성 요소를 원본 소스 확인에 포함할 때까지 발행할 수 없습니다.",
      },
    },
  },
  {
    prefix:
      "Ontology matching requires ActionFlow validation evidence before publish.",
    copy: {
      en: {
        title: "Your app's source changes need behavior validation",
        detail:
          "Attach behavior validation checks for generated or changed action-bearing assets before publishing.",
      },
      ko: {
        title: "앱의 소스 변경 사항은 동작을 검증해야 합니다",
        detail:
          "발행하기 전에 생성했거나 변경한 동작이 포함된 결과물의 동작 검증 검사를 연결하세요.",
      },
    },
  },
  {
    prefix: "Studio generation loop ontology operating-model axis is",
    copy: {
      en: {
        title: "Your app's source selection loop is not ready yet",
        detail:
          "Coogo must prove the generated pages, blocks, and components were selected or backfilled through the source matching contract.",
      },
      ko: {
        title: "앱 구성 요소의 원본 소스 선택 과정이 아직 준비되지 않았습니다",
        detail:
          "생성된 페이지, 블록, 구성 요소가 원본 소스와의 연결 기준에 따라 선택되거나 보완됐는지 확인하세요.",
      },
    },
  },
  {
    prefix: "Figma/template quality ontology operating-model axis is",
    copy: {
      en: {
        title: "Your selected design template is not fully enforced yet",
        detail:
          "Publish requires a tool-enforced template/design-system contract plus source-faithful app view or editable promotion checks.",
      },
      ko: {
        title: "선택한 디자인 템플릿이 아직 도구로 완전히 적용되지 않았습니다",
        detail:
          "발행하려면 도구로 적용되는 템플릿 또는 디자인 시스템 계약과 원본에 충실한 앱 화면 또는 편집 가능한 전환 검사가 필요합니다.",
      },
    },
  },
  {
    prefix: "End-user E2E ontology operating-model axis is",
    copy: {
      en: {
        title: "Your app still needs an end-user creation check",
        detail:
          "Coogo must prove a real user can generate, inspect, and publish this app from chat before this gate is ready.",
      },
      ko: {
        title: "최종 사용자 생성 검사가 필요합니다",
        detail:
          "이 항목을 준비하려면 실제 사용자가 Chat에서 이 앱을 생성하고 확인하고 발행할 수 있는지 검증하세요.",
      },
    },
  },
  {
    prefix: "Report/document parity ontology operating-model axis is",
    copy: {
      en: {
        title: "Your report/document outputs are not parity-ready yet",
        detail:
          "Attach report, document, and native export checks for every claimed output target before publishing.",
      },
      ko: {
        title: "보고서와 문서 출력이 서로 일치하는지 확인해야 합니다",
        detail:
          "발행하기 전에 제공한다고 표시한 모든 출력 대상에 대한 보고서, 문서, 네이티브 내보내기 검사를 연결하세요.",
      },
    },
  },
  {
    prefix: "ActionFlow runtime ontology operating-model axis is",
    copy: {
      en: {
        title: "Your app's behavior check is not ready yet",
        detail:
          "Attach behavior contract validation, runtime simulation, or browser checks before publishing action-bearing assets.",
      },
      ko: {
        title: "앱 동작 검사가 아직 준비되지 않았습니다",
        detail:
          "동작이 포함된 결과물을 발행하기 전에 동작 계약 검증, 런타임 시뮬레이션 또는 브라우저 검사를 연결하세요.",
      },
    },
  },
  {
    prefix: "CI cost policy ontology operating-model axis is",
    copy: {
      en: {
        title: "Your validation cost policy is not ready yet",
        detail:
          "Publish requires confirmation that focused or affected checks run before expensive GitHub Actions gates.",
      },
      ko: {
        title: "검증 비용 정책이 아직 준비되지 않았습니다",
        detail:
          "발행하려면 비용이 큰 GitHub Actions 게이트보다 먼저 대상 또는 변경 영향 검사를 실행하는지 확인해야 합니다.",
      },
    },
  },
  {
    prefix:
      "Selected design system/template must be enforced by a tool compiler or publish gate before publish.",
    copy: {
      en: {
        title: "Your selected design system or template must be tool-enforced",
        detail:
          "The LLM may explain or fill gaps, but the selected template, components, blocks, and style tokens must be locked by a tool contract.",
      },
      ko: {
        title: "선택한 디자인 시스템 또는 템플릿을 도구로 적용해야 합니다",
        detail:
          "LLM은 설명하거나 빈 부분을 채울 수 있지만, 선택한 템플릿·구성 요소·블록·스타일 토큰은 도구 계약으로 고정해야 합니다.",
      },
    },
  },
  {
    prefix:
      "Figma template import needs source-faithful commercial preview or editable-primary promotion evidence before publish.",
    copy: {
      en: {
        title: "Your Figma template needs a source-faithful quality check",
        detail:
          "Publish requires either an app view that matches the source or editable Coogo components promoted with quality checks.",
      },
      ko: {
        title: "Figma 템플릿에 원본과 일치하는 품질 검사가 필요합니다",
        detail:
          "발행하려면 원본과 일치하는 앱 화면 또는 품질 검사를 거쳐 편집 가능하게 전환한 Coogo 구성 요소가 필요합니다.",
      },
    },
  },
  {
    prefix: "CI cost policy readiness is",
    copy: {
      en: {
        title: "Your validation cost policy is not ready yet",
        detail:
          "The attached CI policy must be ready before it can govern publish readiness.",
      },
      ko: {
        title: "검증 비용 정책이 아직 준비되지 않았습니다",
        detail:
          "연결한 CI 정책이 발행 준비 상태에 적용되기 전에 준비되어 있어야 합니다.",
      },
    },
  },
  {
    prefix:
      "CI cost policy must prove focused/affected checks run before expensive GitHub Actions gates.",
    copy: {
      en: {
        title: "Focused checks must run before expensive CI gates",
        detail:
          "Keep local and affected checks first, then reserve heavy GitHub Actions gates for the surfaces that actually changed.",
      },
      ko: {
        title:
          "비용이 큰 CI 게이트보다 먼저 필요한 범위의 검사를 실행해야 합니다",
        detail:
          "로컬 검사와 변경 영향 검사를 먼저 실행하고, 실제로 변경된 영역에만 무거운 GitHub Actions 게이트를 사용하세요.",
      },
    },
  },
  {
    prefix:
      "Dynamic/hybrid publish requires hydrated provider-slot browser verification evidence.",
    copy: {
      en: {
        title: "We still need to confirm your app works in a real browser",
        detail: "Coogo captures this check when it tests your generated app.",
      },
      ko: {
        title: "실제 브라우저에서 앱 동작을 확인해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 이 확인 결과를 수집합니다.",
      },
    },
  },
  {
    prefix:
      "Dynamic/hybrid publish requires passing Studio full-coverage audit evidence.",
    copy: {
      en: {
        title: "We still need to check that every part of your app works",
        detail:
          "Coogo runs this full-coverage audit when it tests your generated app.",
      },
      ko: {
        title: "앱의 모든 부분을 확인해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 전체 범위 감사를 실행합니다.",
      },
    },
  },
  {
    prefix:
      "Dynamic/hybrid publish requires passing Studio exhaustive verification audit evidence.",
    copy: {
      en: {
        title: "We still need to fully test your app before it can go live",
        detail:
          "Coogo runs this exhaustive audit when it tests your generated app.",
      },
      ko: {
        title: "발행하기 전에 앱을 충분히 검사해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 전체 검증 감사를 실행합니다.",
      },
    },
  },
  {
    prefix:
      "Dynamic/hybrid publish requires all runtime-honored actions to be proven or adapter-proven.",
    copy: {
      en: {
        title: "Some of your app's actions still need to be verified",
        detail: "Coogo verifies your actions when it tests your generated app.",
      },
      ko: {
        title: "일부 앱 동작을 아직 검증하지 못했습니다",
        detail: "Coogo가 생성된 앱을 검사할 때 앱 동작을 검증합니다.",
      },
    },
  },
  {
    prefix:
      "Native runtime targets require cross-renderer target matrix evidence before publishing.",
    copy: {
      en: {
        title: "Your app's mobile/native build still needs to be checked",
        detail: "Re-check once the native targets have been built.",
        action: "recheck",
      },
      ko: {
        title: "앱의 모바일 또는 네이티브 빌드를 확인해야 합니다",
        detail: "네이티브 대상을 빌드한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "Native runtime targets are missing target matrix evidence",
    copy: {
      en: {
        title: "Some of your app's native targets haven't been built yet",
        detail: "Re-check once the missing native builds finish.",
        action: "recheck",
      },
      ko: {
        title: "일부 네이티브 대상을 아직 빌드하지 못했습니다",
        detail: "누락된 네이티브 빌드를 마친 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "Native runtime targets remain partial",
    copy: {
      en: {
        title: "Some of your app's native builds aren't finished",
        detail: "Re-check once the native builds complete.",
        action: "recheck",
      },
      ko: {
        title: "일부 네이티브 빌드를 아직 마치지 못했습니다",
        detail: "네이티브 빌드를 마친 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix: "Native runtime target",
    copy: {
      en: {
        title: "Your app's native build needs more checks",
        detail: "Re-check once the native build passes every check.",
        action: "recheck",
      },
      ko: {
        title: "앱의 네이티브 빌드에 추가 검사가 필요합니다",
        detail: "네이티브 빌드가 모든 검사를 통과한 뒤 다시 확인하세요.",
        action: "recheck",
      },
    },
  },
  {
    prefix:
      "Studio Refero batch gate is blocked by end-user verification evidence.",
    copy: {
      en: {
        title: "Your app's quality review is still in progress",
        detail: "This clears once Coogo finishes the quality review.",
      },
      ko: {
        title: "앱 품질 검토 결과가 아직 준비되지 않았습니다",
        detail: "품질 검토 결과가 준비된 뒤 발행 준비 상태를 다시 확인하세요.",
      },
    },
  },
  {
    prefix: "Dynamic/hybrid publish blocks",
    copy: {
      en: {
        title:
          "Your app still needs to pass an automated check before going live",
        detail: "Coogo captures this check when it tests your generated app.",
      },
      ko: {
        title: "발행하기 전에 앱이 자동 검사를 통과해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 이 확인 결과를 수집합니다.",
      },
    },
  },
  {
    prefix: "Dynamic/hybrid publish cannot proceed",
    copy: {
      en: {
        title:
          "Your app still needs to pass an automated check before going live",
        detail: "Coogo captures this check when it tests your generated app.",
      },
      ko: {
        title: "발행하기 전에 앱이 자동 검사를 통과해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 이 확인 결과를 수집합니다.",
      },
    },
  },
  {
    prefix: "Dynamic/hybrid publish rejects",
    copy: {
      en: {
        title:
          "Your app still needs to pass an automated check before going live",
        detail: "Coogo captures this check when it tests your generated app.",
      },
      ko: {
        title: "발행하기 전에 앱이 자동 검사를 통과해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 이 확인 결과를 수집합니다.",
      },
    },
  },
  {
    prefix: "Dynamic/hybrid publish requires",
    copy: {
      en: {
        title:
          "Your app still needs to pass an automated check before going live",
        detail: "Coogo captures this check when it tests your generated app.",
      },
      ko: {
        title: "발행하기 전에 앱이 자동 검사를 통과해야 합니다",
        detail: "Coogo가 생성된 앱을 검사할 때 이 확인 결과를 수집합니다.",
      },
    },
  },
  {
    prefix: "Cross-block chain integrity errors detected",
    copy: {
      en: {
        title: "Some of your app's screens don't connect correctly yet",
        detail:
          "Open Chat to fix the broken links between screens, then publish again.",
        action: "ask-cogo",
      },
      ko: {
        title: "일부 앱 화면이 아직 올바르게 연결되지 않았습니다",
        detail: "Chat에서 화면 사이의 끊어진 연결을 수정한 뒤 다시 발행하세요.",
        action: "ask-cogo",
      },
    },
  },
  {
    prefix: "Block '",
    copy: {
      en: {
        title:
          "One of your app's sections needs more work before it can go live",
        detail: "Open Chat to improve it, then publish again.",
        action: "ask-cogo",
      },
      ko: {
        title: "앱의 한 영역을 더 보완해야 합니다",
        detail: "Chat에서 해당 영역을 개선한 뒤 다시 발행하세요.",
        action: "ask-cogo",
      },
    },
  },
];

export const SERVER_QUALITY_GATE_PRODUCER_REASONS = {
  CROSS_BLOCK_CHAIN: "Cross-block chain integrity errors detected",
  BLOCK_SCORE: "Block 'hero' score 40 < 80 (grade: reject)",
} as const;

export function humanizePublishReason(
  reason: string,
  locale: LocaleCode,
): HumanizedPublishReason {
  const resolvedLocale = presentationLocale(locale);
  const trimmed = reason.trim();
  const entry = PUBLISH_REASON_CATALOG.find(({ prefix }) =>
    trimmed.startsWith(prefix),
  );
  if (!entry) {
    return resolvedLocale === "ko"
      ? {
          title: "발행 준비 상태를 확인해 주세요",
          detail:
            "발행 전에 해결해야 할 항목이 있습니다. 준비 상태에서 세부 내용을 확인하세요.",
        }
      : { title: reason };
  }
  const copy = entry.copy[resolvedLocale];
  return {
    title: copy.title,
    ...(copy.detail ? { detail: copy.detail } : {}),
    ...(copy.action
      ? {
          action: {
            kind: copy.action,
            // Real `locale`, not `resolvedLocale` — the button label covers
            // all 5 Studio locales even though title/detail above are still
            // bilingual-only.
            label: ACTION_LABELS[locale][copy.action],
          },
        }
      : {}),
  };
}

export function hasCuratedPublishReasonCopy(reason: string): boolean {
  return PUBLISH_REASON_CATALOG.some(({ prefix }) =>
    reason.trim().startsWith(prefix),
  );
}
